import { lazy, Suspense } from 'react';
import { Navigate, Route } from 'react-router';
import { Loader2 } from 'lucide-react';
import { FEATURE_FLAGS } from '@/lib/featureFlags';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { RequireAdminRoute } from '@/components/auth/RequireAdminRoute';
import { RouteGuard } from '@/components/auth/RouteGuard';

/**
 * BillingIndexRedirect chooses the billing landing tab. Reconciliation is the
 * landing only for admins (issue #906) — the Reconciliation overview is an
 * owner-only finance/analytics surface. Non-admins (or the flag being off) land
 * on Invoices, a staff-accessible transactional surface.
 */
function BillingIndexRedirect() {
  const isAdmin = useIsAdmin();
  const to = FEATURE_FLAGS.reconciliation && isAdmin ? 'reconciliation' : 'invoices';
  return <Navigate to={to} replace />;
}

// Lazy load billing components for code splitting
const BillingPage = lazy(() => import('./BillingPage'));
const InvoicesPage = lazy(() => import('./InvoicesPage'));
const EmailCockpitPage = lazy(() => import('./EmailCockpitPage'));
const DunningCockpitPage = lazy(() => import('./DunningCockpitPage'));
const ReconciliationPage = lazy(() => import('./ReconciliationPage'));

// Loading component for suspense fallback
function BillingLoadingFallback() {
  return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
    </div>
  );
}

/**
 * Billing routes configuration
 * Provides nested routing structure for billing module
 */
export const billingRoutes = (
  <Route
    path="billing"
    element={
      // The `billing` module guards the whole billing tree (issue #1670).
      <RouteGuard path="/billing">
        <Suspense fallback={<BillingLoadingFallback />}>
          <BillingPage />
        </Suspense>
      </RouteGuard>
    }
  >
    {/*
      The billing Dashboard tab was retired — the Reconciliation overview is now
      the billing landing (for admins only, issue #906). Redirect the index to it
      when the feature flag is on AND the caller is an admin; otherwise fall back
      to Invoices (Reconciliation isn't mounted when off / for non-admins).
    */}
    <Route index element={<BillingIndexRedirect />} />
    <Route
      path="invoices"
      element={
        <Suspense fallback={<BillingLoadingFallback />}>
          <InvoicesPage />
        </Suspense>
      }
    />
    <Route
      path="email-cockpit"
      element={
        <Suspense fallback={<BillingLoadingFallback />}>
          <EmailCockpitPage />
        </Suspense>
      }
    />
    <Route
      path="dunning"
      element={
        <Suspense fallback={<BillingLoadingFallback />}>
          <DunningCockpitPage />
        </Suspense>
      }
    />
    {FEATURE_FLAGS.reconciliation && (
      <Route
        path="reconciliation"
        element={
          <RequireAdminRoute fallback="/billing/invoices">
            <Suspense fallback={<BillingLoadingFallback />}>
              <ReconciliationPage />
            </Suspense>
          </RequireAdminRoute>
        }
      />
    )}
  </Route>
);

export default billingRoutes;